import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { finalize } from 'rxjs';
import {
  ApPaymentRequest,
  ApPaymentRequestSearchCommand,
  ApPaymentRequestStatus,
} from '../../../models/ap-payment-request/ap-payment-request';
import { ApPaymentRequestProxyService } from '../../../proxies/ap-payment-request.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { ProviderFilterService } from '../../../services/provider-filter.service';

@Component({
  selector: 'app-ap-payment-requests',
  templateUrl: './ap-payment-requests.component.html',
  styleUrl: './ap-payment-requests.component.scss',
})
export class ApPaymentRequestsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly service = inject(ApPaymentRequestProxyService);
  private readonly enumService = inject(EnumService);
  private readonly providerFilter = inject(ProviderFilterService);
  private readonly translateService = inject(TranslateService);

  isLoading = signal(0);
  requests = signal<ApPaymentRequest[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);

  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'ap-payment-requests-filters', config: this.filterConfig });

  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};
  /** Criteria of the current result set — replayed on paging so a page change keeps the filters. */
  private lastSearchCriteria: ApPaymentRequestSearchCommand = {};

  /** This screen's own type-ahead state — see ProviderFilterService.createConfig. */
  private readonly providerAutocompleteConfig = this.providerFilter.createConfig(inject(DestroyRef));

  constructor() {
    this.filterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchWithFilters(filters as ApPaymentRequestSearchCommand));
  }

  ngOnInit(): void {
    this.statusLabels = this.enumService.getEnum('ApPaymentRequestStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;

    this.filterConfig.set(this.buildFilterConfig());
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: this.translate('ApPaymentRequest_Number', 'Request #'),
          placeholder: this.translate('ApPaymentRequests_Search', 'Enter request number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'invoiceNumber',
          label: this.translate('ApPayment_InvoiceNumber', 'Invoice #'),
          placeholder: this.translate('ApPayments_Filter_Invoice', 'AP or provider invoice number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'providerReference',
          label: this.translate('ApPayment_Provider', 'Provider'),
          placeholder: this.translate('Provider_Search', 'Search provider'),
          autocompleteConfig: this.providerAutocompleteConfig,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.translate('Status', 'Status'),
          placeholder: this.translate('Select_status', 'Select status'),
          options: this.statusLabels,
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private search(criteria: ApPaymentRequestSearchCommand): void {
    this.lastSearchCriteria = criteria;
    this.startLoading();
    this.service
      .search(criteria, this.currentPage(), this.pageSize())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.requests.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.requests.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: ApPaymentRequestSearchCommand): void {
    // A persisted/URL filter arrives as a string — the API types status as an enum, so coerce it back.
    const criteria: ApPaymentRequestSearchCommand = {
      ...filters,
      number: filters.number || null,
      invoiceNumber: filters.invoiceNumber || null,
      // The type-ahead puts the whole suggestion in the control, so the spread above would
      // send an object where the API expects the id.
      providerReference: this.providerFilter.toProviderId(filters.providerReference),
      status: filters.status != null ? (Number(filters.status) as ApPaymentRequestStatus) : null,
    };
    this.currentPage.set(1);
    this.search(criteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.lastSearchCriteria);
  }

  redirectToDetails(id?: string): void {
    this.router.navigate(['/ap-payment-request-details'], { queryParams: { id: id ?? 'new' } });
  }

  /**
   * The documents a request pays, named once each — our AP number first, the provider's printed one only
   * where a line has none. Read from the lines' own snapshot, so a request whose documents were since paid
   * still names them. Same rule as the AP payments list.
   */
  invoiceNumbers(request: ApPaymentRequest): string[] {
    const numbers = (request.lines ?? []).map((l) => l.invoiceNumber || l.fiscalNumber || '').filter((n) => n !== '');
    return [...new Set(numbers)];
  }

  statusLabel(status: number): string {
    return this.statusLabels[status] ?? '';
  }

  statusSeverity(status: number): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case ApPaymentRequestStatus.Approved:
        return 'success';
      case ApPaymentRequestStatus.PendingApproval:
        return 'warn';
      case ApPaymentRequestStatus.Rejected:
        return 'danger';
      case ApPaymentRequestStatus.Cancelled:
        return 'secondary';
      default:
        return 'info';
    }
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  getRowData(rowData: unknown): ApPaymentRequest {
    return rowData as ApPaymentRequest;
  }
}
